import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { buildManufacturingBatchDetailsPath } from "@/lib/manufacturingEditBatchPath";
import {
  buildManufacturingWorkOrderDetailPath,
  buildMrpPurchaseInvoiceDetailPath,
  buildMrpPurchaseOrderDetailPath,
  buildMrpSupplierDetailPath,
  buildSalesOrderDetailPath,
  buildShippingGosiDetailsPath,
  buildShippingShipmentDetailsPath,
} from "@/lib/hubEntityDetailPaths";
import { cn } from "@/lib/utils";

import { DetailLinkWithEntityPeek } from "./DetailLinkWithEntityPeek";
import { useEntityPeekPrefetchHandlers } from "./EntityPeekHint";
import type {
  BatchPeekContext,
  EntityPeekProps,
  GosiPeekContext,
  PurchaseOrderPeekContext,
  ShipmentPeekContext,
  SupplierPeekContext,
} from "./entityPeekTypes";
import type { SalesOrderPeekContext } from "./SalesOrderPeekHoverHintBody";

const DETAIL_LINK_CLASS =
  "font-medium text-sky-700 hover:text-sky-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1";

export function BatchDetailLink({
  batchId,
  className,
  children,
  peek,
}: {
  batchId: number;
  className?: string;
  children?: ReactNode;
  peek?: BatchPeekContext;
}) {
  const label = children ?? `Batch ${String(batchId)}`;
  const peekProps: EntityPeekProps =
    peek != null ? { kind: "batch", batchId, context: peek } : { kind: "batch", batchId };
  const prefetchHandlers = useEntityPeekPrefetchHandlers(peekProps);
  return (
    <DetailLinkWithEntityPeek
      peek={peekProps}
      trigger={
        <Link
          to={buildManufacturingBatchDetailsPath(batchId)}
          className={cn(DETAIL_LINK_CLASS, className)}
          {...prefetchHandlers}
        >
          {label}
        </Link>
      }
    />
  );
}

export function SalesOrderDetailLink({
  salesOrderId,
  salesOrderNumber,
  className,
  children,
  peek,
  block = false,
}: {
  salesOrderId: number;
  salesOrderNumber?: string | null;
  className?: string;
  children?: ReactNode;
  peek?: SalesOrderPeekContext;
  block?: boolean;
}) {
  const label =
    children ??
    (salesOrderNumber != null && salesOrderNumber.trim() !== ""
      ? salesOrderNumber.trim()
      : `Sales order ${String(salesOrderId)}`);
  const mergedPeek =
    peek != null ? { ...peek, sales_order_number: peek.sales_order_number ?? salesOrderNumber } : undefined;
  const peekProps: EntityPeekProps =
    mergedPeek != null
      ? { kind: "sales_order", salesOrderId, context: mergedPeek }
      : { kind: "sales_order", salesOrderId };
  const prefetchHandlers = useEntityPeekPrefetchHandlers(peekProps);
  return (
    <DetailLinkWithEntityPeek
      block={block}
      peek={peekProps}
      trigger={
        <Link
          to={buildSalesOrderDetailPath(salesOrderId)}
          className={cn(DETAIL_LINK_CLASS, className)}
          {...prefetchHandlers}
        >
          {label}
        </Link>
      }
    />
  );
}

export function PurchaseOrderDetailLink({
  purchaseOrderId,
  purchaseOrderNumber,
  className,
  children,
  peek,
}: {
  purchaseOrderId: number;
  purchaseOrderNumber?: string | null;
  className?: string;
  children?: ReactNode;
  peek?: PurchaseOrderPeekContext;
}) {
  const label =
    children ??
    (purchaseOrderNumber != null && purchaseOrderNumber.trim() !== ""
      ? purchaseOrderNumber.trim()
      : `Purchase order ${String(purchaseOrderId)}`);
  const peekProps: EntityPeekProps = {
    kind: "purchase_order",
    purchaseOrderId,
    context:
      peek != null
        ? {
            ...peek,
            purchase_order_number: peek.purchase_order_number ?? purchaseOrderNumber,
          }
        : { purchase_order_number: purchaseOrderNumber },
  };
  const prefetchHandlers = useEntityPeekPrefetchHandlers(peekProps);
  return (
    <DetailLinkWithEntityPeek
      peek={peekProps}
      trigger={
        <Link
          to={buildMrpPurchaseOrderDetailPath(purchaseOrderId)}
          className={cn(DETAIL_LINK_CLASS, className)}
          {...prefetchHandlers}
        >
          {label}
        </Link>
      }
    />
  );
}

export function SupplierDetailLink({
  supplierId,
  supplierName,
  className,
  children,
  peek,
}: {
  supplierId: number;
  supplierName?: string | null;
  className?: string;
  children?: ReactNode;
  peek?: SupplierPeekContext;
}) {
  const label =
    children ??
    (supplierName != null && supplierName.trim() !== "" ? supplierName.trim() : `Supplier ${String(supplierId)}`);
  const peekProps: EntityPeekProps = {
    kind: "supplier",
    supplierId,
    context:
      peek != null
        ? {
            ...peek,
            supplier_name: peek.supplier_name ?? supplierName,
          }
        : { supplier_name: supplierName },
  };
  const prefetchHandlers = useEntityPeekPrefetchHandlers(peekProps);
  return (
    <DetailLinkWithEntityPeek
      peek={peekProps}
      trigger={
        <Link
          to={buildMrpSupplierDetailPath(supplierId)}
          className={cn(DETAIL_LINK_CLASS, className)}
          {...prefetchHandlers}
        >
          {label}
        </Link>
      }
    />
  );
}

export function ShipmentDetailLink({
  shipmentId,
  className,
  children,
  peek,
}: {
  shipmentId: number;
  className?: string;
  children?: ReactNode;
  peek?: ShipmentPeekContext;
}) {
  const label = children ?? `Shipment ${String(shipmentId)}`;
  const peekProps: EntityPeekProps =
    peek != null ? { kind: "shipment", shipmentId, context: peek } : { kind: "shipment", shipmentId };
  const prefetchHandlers = useEntityPeekPrefetchHandlers(peekProps);
  return (
    <DetailLinkWithEntityPeek
      peek={peekProps}
      trigger={
        <Link
          to={buildShippingShipmentDetailsPath(shipmentId)}
          className={cn(DETAIL_LINK_CLASS, className)}
          {...prefetchHandlers}
        >
          {label}
        </Link>
      }
    />
  );
}

export function GosiDetailLink({
  mvDocumentId,
  gosiDocumentNumber,
  className,
  children,
  peek,
}: {
  mvDocumentId: number;
  gosiDocumentNumber?: string | null;
  className?: string;
  children?: ReactNode;
  peek?: GosiPeekContext;
}) {
  const label =
    children ??
    (gosiDocumentNumber != null && gosiDocumentNumber.trim() !== ""
      ? gosiDocumentNumber.trim()
      : `GOSI ${String(mvDocumentId)}`);
  const peekProps: EntityPeekProps | undefined =
    peek != null
      ? {
          kind: "gosi",
          context: {
            ...peek,
            mv_document_id: peek.mv_document_id ?? mvDocumentId,
            document_number: peek.document_number ?? gosiDocumentNumber,
          },
        }
      : undefined;
  return (
    <DetailLinkWithEntityPeek
      peek={peekProps}
      trigger={
        <Link to={buildShippingGosiDetailsPath(mvDocumentId)} className={cn(DETAIL_LINK_CLASS, className)}>
          {label}
        </Link>
      }
    />
  );
}

export function PurchaseInvoiceDetailLink({
  purchaseInvoiceId,
  mvNumber,
  vendorInvoiceNumber,
  className,
  children,
}: {
  purchaseInvoiceId: number;
  mvNumber?: string | null;
  vendorInvoiceNumber?: string | null;
  className?: string;
  children?: ReactNode;
}) {
  const label =
    children ??
    (mvNumber != null && mvNumber.trim() !== "" ? mvNumber.trim() : `GIPI #${String(purchaseInvoiceId)}`);
  const peek: EntityPeekProps = {
    kind: "gipi",
    purchaseInvoiceId,
    vendorInvoiceNumber,
  };
  const prefetchHandlers = useEntityPeekPrefetchHandlers(peek);
  return (
    <DetailLinkWithEntityPeek
      peek={peek}
      trigger={
        <Link
          to={buildMrpPurchaseInvoiceDetailPath(purchaseInvoiceId)}
          className={cn(DETAIL_LINK_CLASS, className)}
          {...prefetchHandlers}
        >
          {label}
        </Link>
      }
    />
  );
}

export function WorkOrderDetailLink({
  buildOrderId,
  workOrderNumber,
  className,
  children,
}: {
  buildOrderId: number;
  workOrderNumber?: string | null;
  className?: string;
  children?: ReactNode;
}) {
  const label =
    children ??
    (workOrderNumber != null && workOrderNumber.trim() !== ""
      ? workOrderNumber.trim()
      : `Work order ${String(buildOrderId)}`);
  const peek: EntityPeekProps = { kind: "work_order", buildOrderId };
  const prefetchHandlers = useEntityPeekPrefetchHandlers(peek);
  return (
    <DetailLinkWithEntityPeek
      peek={peek}
      trigger={
        <Link
          to={buildManufacturingWorkOrderDetailPath(buildOrderId)}
          className={cn(DETAIL_LINK_CLASS, className)}
          {...prefetchHandlers}
        >
          {label}
        </Link>
      }
    />
  );
}
